import { BEIGE_400, BEIGE_600, BOX_SHADOW_XS, GRAY_700, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material'
import { FC, ReactNode, useMemo } from 'react'
import { PropertyTableColumn, propertiesTableHead } from 'constants/propertiesDashboard'
import { SortBy, SortDirection } from 'constants/misc'

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { MUICheckbox } from 'components/common/MUICheckBox'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { useCanBulkActOnProperties } from '../contexts'
import { usePropertiesDashboard } from '../contexts/PropertiesDashboard.context'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props
 */
interface Props {
  /** Custom table row in the table body. */
  children?: ReactNode
  /** Current sort by field */
  sortBy?: SortBy
  /** Current sort direction */
  sortDirection?: SortDirection
  /** Callback when sort header is clicked */
  onSortChange?: (sortBy: SortBy) => void
  /** IDs of properties currently rendered — used by header "select all" checkbox. */
  visibleIds?: string[]
}

/**
 * @component Properties dashboard table
 * 
 * Sorting behavior:
 * - Default: All sortable columns show SwapVert icon, no sorting applied
 * - Active sort: Only one column sorted at a time, shows ArrowUpward/ArrowDownward
 * - Click: Sets column as active (ascending default), click again to toggle direction
 * - Sortable columns: Title (alphabetical), Created date (chronological)
 * 
 * @example
 * <PropertiesDashboardTable />
 */
export const PropertiesDashboardTable: FC<Props> = ({ children, sortBy, sortDirection, onSortChange, visibleIds = [] }) => {
  const { t } = useTranslation(['properties_dashboard'])
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { isSelected, isSelectionFull, selectMany, deselectMany } = usePropertiesDashboard()
  const showCheckbox = useCanBulkActOnProperties()

  const visibleSelectedCount = visibleIds.filter(isSelected).length
  const allVisibleSelected = visibleIds.length > 0
    && (visibleSelectedCount === visibleIds.length || (isSelectionFull && visibleSelectedCount > 0))
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected

  const handleToggleAllVisible = () => {
    if (allVisibleSelected) {
      deselectMany(visibleIds)
    } else {
      selectMany(visibleIds)
    }
  }

  const getSortIcon = (columnSortBy: SortBy) => {
    // If this column is the active sort column, show direction arrow
    if (sortBy === columnSortBy) {
      return sortDirection === SortDirection.ASCENDING
        ? <ArrowUpwardIcon fontSize="small" />
        : <ArrowDownwardIcon fontSize="small" />
    }
    // Otherwise show SwapVert icon
    return <SwapVertIcon fontSize="small" />
  }

  const handleSortClick = (columnSortBy: SortBy) => {
    if (onSortChange) {
      onSortChange(columnSortBy)
    }
  }

  const isSortableColumn = (title: PropertyTableColumn) => {
    return [PropertyTableColumn.TITLE, PropertyTableColumn.CREATED_AT, PropertyTableColumn.LAST_UPDATED_AT].includes(title)
  }

  const getColumnSortBy = (title: PropertyTableColumn): SortBy | null => {
    switch (title) {
      case PropertyTableColumn.TITLE:
        return SortBy.PROPERTY_TITLE
      case PropertyTableColumn.CREATED_AT:
        return SortBy.PROPERTY_CREATION_DATE
      case PropertyTableColumn.LAST_UPDATED_AT:
        return SortBy.LAST_UPDATED_DATE
      default:
        return null
    }
  }

  const getColumnWidth = (title: PropertyTableColumn): string => {
    switch (title) {
      case PropertyTableColumn.CHECKBOX:
        return '5%' // Checkbox needs minimal space
      case PropertyTableColumn.TITLE:
        return '25%' // Title needs more space for longer property names
      case PropertyTableColumn.ADDRESS:
        return '30%' // Address needs the most space for full addresses
      case PropertyTableColumn.CREATED_AT:
        return '15%' // Date needs less space
      case PropertyTableColumn.LAST_UPDATED_AT:
        return '15%' // Date needs less space
      case PropertyTableColumn.PROPERTY_MANAGER:
        return '20%' // Manager name needs moderate space
      case PropertyTableColumn.ACTION:
        return '10%' // Actions need minimal space for buttons
      default:
        return 'auto'
    }
  }

  const getColumnWidthMobileSize = (title: PropertyTableColumn): string => {
    switch (title) {
      case PropertyTableColumn.CHECKBOX:
        return '4rem' // Checkbox needs minimal space
      case PropertyTableColumn.TITLE:
        return '24rem' // Title needs more space for longer property names
      case PropertyTableColumn.ADDRESS:
        return '24rem' // Address needs the most space for full addresses
      case PropertyTableColumn.CREATED_AT:
        return '12rem' // Date needs less space
      case PropertyTableColumn.LAST_UPDATED_AT:
        return '12rem' // Last updated date needs moderate space
      case PropertyTableColumn.PROPERTY_MANAGER:
        return '14rem' // Manager name needs moderate space
      case PropertyTableColumn.ACTION:
        return '6rem' // Actions need minimal space for buttons
      default:
        return 'auto'
    }
  }

  return (
    <Table
      size="medium"
      sx={{
        borderRadius: '.8rem',
        backgroundColor: WHITE,
        border: `.1rem solid ${BEIGE_600}`,
        boxShadow: BOX_SHADOW_XS,
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        width: '100%',
      }}
    >

      <TableHead>
        <TableRow sx={{
          boxShadow: '0px 1px 4px 0px rgba(19, 24, 29, 0.06)',
          position: 'sticky',
          top: -1,
          zIndex: 10,
        }}>
          {propertiesTableHead.filter(title => showCheckbox || title !== PropertyTableColumn.CHECKBOX).map(title => {
            const columnSortBy = getColumnSortBy(title)
            const isSortable = isSortableColumn(title)
            const isCheckbox = title === PropertyTableColumn.CHECKBOX

            return (
              <TableCell
                key={title}
                sx={{
                  width: isMobileView ? getColumnWidthMobileSize(title) : getColumnWidth(title),
                  minWidth: isMobileView ? getColumnWidthMobileSize(title) : getColumnWidth(title),
                  maxWidth: isMobileView ? getColumnWidthMobileSize(title) : getColumnWidth(title),
                  '&:last-child': { textAlign: 'right' },
                  backgroundColor: BEIGE_400,
                  cursor: isSortable ? 'pointer' : 'default',
                  '&:hover': isSortable ? {
                    backgroundColor: BEIGE_600,
                  } : {},
                }}
                onClick={isSortable && columnSortBy ? () => handleSortClick(columnSortBy) : undefined}
              >
                {isCheckbox ? (
                  <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
                    <MUICheckbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      disabled={visibleIds.length === 0}
                      onClick={handleToggleAllVisible}
                    />
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap="0.4rem">
                    {title !== PropertyTableColumn.ACTION && (
                      <Typography
                        variant="text-xs"
                        fontWeight={600}
                        color={GRAY_700}
                        sx={{
                          fontWeight: sortBy === columnSortBy ? 700 : 600,
                        }}
                      >
                        {t(`properties_table.${title}`)}
                      </Typography>
                    )}
                    {isSortable && getSortIcon(columnSortBy!)}
                  </Box>
                )}
              </TableCell>
            )
          })}
        </TableRow>
      </TableHead>

      <TableBody>
        {children}
      </TableBody>

    </Table>
  )
}
